'use client';

import type { ButtonHTMLAttributes, ReactNode, Ref } from 'react';
import { cx } from './cx';

export type KButtonTone = 'plain' | 'primary' | 'danger' | 'link';
export type KButtonSize = 'md' | 'sm';

type Props = Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'className'> & {
  tone?: KButtonTone;
  size?: KButtonSize;
  /** Показывает крутилку и блокирует повторные нажатия. Ширина кнопки не прыгает:
   *  подпись остаётся на месте, крутилка встаёт поверх. */
  loading?: boolean;
  /** Значок слева от подписи. Только наша подсистема значков — эмодзи запрещены. */
  icon?: ReactNode;
  /** Растянуть на всю ширину родителя (кнопка формы под полем). */
  block?: boolean;
  /** РОЛЬ «КНОПКА ДЕЙСТВИЯ» внизу страницы/листа/карточки (ТЗ WEB-CAUSES М33): во всю ширину
   *  колонки содержимого, капсула 52, слово 17/600 — одна геометрия для всех тонов
   *  (`.k-btn--action` в kit.css). Для KBareButton со своим тоном — тот же класс. */
  action?: boolean;
  className?: string;
  children?: ReactNode;
};

/** Кнопка набора. Вид — `.k-btn` в kit.css: капсула по родному списку iOS (Э4.1, 30.09.2026).
 *  Тона: `plain` — серая капсула («призрак», отмена), `primary` — главная (акцент),
 *  `danger` — красная капсула с белым словом, `link` — ссылка-кнопка без фона
 *  («Изменить», «Отправить снова»); красное слово без заливки — `link` + класс `is-danger`. */
export function KButton({
  tone = 'plain',
  size = 'md',
  loading = false,
  icon,
  block = false,
  action = false,
  className,
  children,
  disabled,
  type = 'button',
  ...rest
}: Props) {
  return (
    <button
      {...rest}
      type={type}
      // Во время загрузки кнопка не исчезает из-под курсора и остаётся озвученной
      // скринридером — поэтому aria-busy и disabled, а не подмена на заглушку.
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={cx(
        'k k-ctl k-btn',
        `k-btn--${tone}`,
        size === 'sm' && 'k-btn--sm',
        block && 'k-btn--block',
        action && 'k-btn--action',
        loading && 'is-loading',
        className,
      )}
    >
      {icon ? <span className="k-btn__icon">{icon}</span> : null}
      {/* Кнопка ТОЛЬКО со значком (кружок «+» страницы) — пустой обёртки подписи не
          рисуем: кружок сложен inline-grid, и пустой span вставал второй строкой сетки,
          сдвигая глиф вверх от центра (владелец 03.09.2026: «плюс кривой»). */}
      {children == null || children === '' || children === false ? null : (
        <span className="k-btn__label">{children}</span>
      )}
      {loading ? <span className="k-btn__spin" aria-hidden="true" /> : null}
    </button>
  );
}

type BareProps = Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'className'> & {
  className?: string;
  children?: ReactNode;
  /** Узел кнопки — тем, кто меряет её место (подтверждение ищет модалку-хозяйку). */
  ref?: Ref<HTMLButtonElement>;
};

/**
 * Кнопка БЕЗ ВИДА — для нажимаемых мест, которые оформляет сама страница: значок
 * (звезда избранного, «скопировать»), кружок оттенка, кликабельная карточка. Вид даёт
 * класс страницы, элемент только гасит браузерную кнопку и добавляет клавиатурный
 * фокус (`.k-bare` в kit.css).
 *
 * Разметка остаётся ТОЙ ЖЕ: тот же `<button>`, те же классы, дети без обёрток — поэтому
 * переезд на этот элемент не двигает вид ни на пиксель.
 *
 * НЕ лазейка мимо набора: если место выглядит как обычная кнопка (подпись, рамка,
 * заливка) — это `KButton`, а не эта. Сюда — только то, чего в наборе нет и быть не
 * может, потому что вид у каждого свой.
 */
export function KBareButton({ className, children, type = 'button', ...rest }: BareProps) {
  return (
    <button {...rest} type={type} className={cx('k k-bare', className)}>
      {children}
    </button>
  );
}

type PillProps = Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'className'> & {
  /** `md` — рядом с кружком 44 (шапка листа и страницы); `sm` — рядом с кружком 36. */
  size?: 'md' | 'sm';
  className?: string;
  children?: ReactNode;
  ref?: Ref<HTMLButtonElement>;
};

/**
 * ПИЛЮЛЯ-СЛОВО ШАПКИ — ОДНА НА ВЕСЬ ВЕБ (владелец 04.10.2026, пакет Д2; UI-TEMPLATE-CANON
 * Закон 5 п.8): «Изм.», «Готово», «Выбрать», «Закрыть», «Отмена», «Далее»… в шапке листа,
 * панели, колонки или страницы. Вид — `.k-pill` в kit.css: стеклянный кружок `.k-round`,
 * растянутый под слово (та же поверхность, та же высота, слово 17/400 чернилами, поля 16).
 * Своих классов вида у слов шапок больше нет — место (сетка шапки, позиция) даёт страница
 * классом, вид не трогает.
 */
export function KPill({ size = 'md', className, children, type = 'button', ...rest }: PillProps) {
  return (
    <button {...rest} type={type} className={cx('k k-pill', size === 'sm' && 'k-pill--sm', className)}>
      {children}
    </button>
  );
}
